import { lazy, Suspense, useEffect } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Index from "./pages/Index";
import ScrollToTop from "./components/ScrollToTop";
import CookieConsent from "./components/CookieConsent";
import { initConsent } from "./lib/consent";

// Home is loaded eagerly (LCP route). Everything else is lazy — keeps the
// initial JS bundle small so PageSpeed's critical path stays short.
const PsicoterapiaInfantil = lazy(() => import("./pages/PsicoterapiaInfantil"));
const PsicoterapiaAdolescente = lazy(() => import("./pages/PsicoterapiaAdolescente"));
const PsicoterapiaAdulto = lazy(() => import("./pages/PsicoterapiaAdulto"));
const PsicoterapiaCasal = lazy(() => import("./pages/PsicoterapiaCasal"));
const PsicoterapiaMelhorIdade = lazy(() => import("./pages/PsicoterapiaMelhorIdade"));
const PsicoterapiaBreve = lazy(() => import("./pages/PsicoterapiaBreve"));
const PsicoterapiaTCC = lazy(() => import("./pages/PsicoterapiaTCC"));
const AvaliacaoNeuropsicologica = lazy(() => import("./pages/AvaliacaoNeuropsicologica"));
const AvaliacaoLaudoCirurgia = lazy(() => import("./pages/AvaliacaoLaudoCirurgia"));
const AvaliacaoPsicodiagnostico = lazy(() => import("./pages/AvaliacaoPsicodiagnostico"));
const PericiaPsicologica = lazy(() => import("./pages/PericiaPsicologica"));
const OrientacaoVocacional = lazy(() => import("./pages/OrientacaoVocacional"));
const ReorientacaoCarreira = lazy(() => import("./pages/ReorientacaoCarreira"));
const Contato = lazy(() => import("./pages/Contato"));
const Equipe = lazy(() => import("./pages/Equipe"));
const GuiaAnsiedade = lazy(() => import("./pages/GuiaAnsiedade"));
const PoliticaPrivacidade = lazy(() => import("./pages/PoliticaPrivacidade"));
const Blog = lazy(() => import("./pages/Blog"));
const AnsiedadeSintomasFisicos = lazy(() => import("./pages/blog/AnsiedadeSintomasFisicos"));
const BlogCategory = lazy(() => import("./pages/BlogCategory"));
const BlogPostArchive = lazy(() => import("./pages/BlogPostArchive"));
const Ebooks = lazy(() => import("./pages/Ebooks"));
const EbookPost = lazy(() => import("./pages/EbookPost"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const RouteFallback = () => (
  <div className="min-h-screen bg-background" aria-hidden="true" />
);

const App = () => {
  useEffect(() => {
    initConsent();
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <ScrollToTop />

          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/psicoterapia-infantil" element={<PsicoterapiaInfantil />} />
              <Route path="/psicoterapia-adolescente" element={<PsicoterapiaAdolescente />} />
              <Route path="/psicoterapia-adulto" element={<PsicoterapiaAdulto />} />
              <Route path="/psicoterapia-casal" element={<PsicoterapiaCasal />} />
              <Route path="/psicoterapia-melhor-idade" element={<PsicoterapiaMelhorIdade />} />
              <Route path="/psicoterapia-breve" element={<PsicoterapiaBreve />} />
              <Route path="/tcc" element={<PsicoterapiaTCC />} />
              <Route path="/avaliacao-neuropsicologica" element={<AvaliacaoNeuropsicologica />} />
              <Route path="/avaliacao-laudo-cirurgia" element={<AvaliacaoLaudoCirurgia />} />
              <Route path="/avaliacao-psicodiagnostico" element={<AvaliacaoPsicodiagnostico />} />
              <Route path="/pericia-psicologica" element={<PericiaPsicologica />} />
              <Route path="/orientacao-vocacional" element={<OrientacaoVocacional />} />
              <Route path="/reorientacao-carreira" element={<ReorientacaoCarreira />} />
              <Route path="/contato" element={<Contato />} />
              <Route path="/equipe" element={<Equipe />} />
              <Route path="/guia-ansiedade" element={<GuiaAnsiedade />} />
              <Route path="/politica-de-privacidade" element={<PoliticaPrivacidade />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/categoria/:slug" element={<BlogCategory />} />
              <Route path="/blog/sintomas-fisicos-ansiedade" element={<AnsiedadeSintomasFisicos />} />
              <Route path="/blog/:slug" element={<BlogPostArchive />} />
              <Route path="/ebooks" element={<Ebooks />} />
              <Route path="/ebooks/:slug" element={<EbookPost />} />
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <CookieConsent />
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
